import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { encryptAppId, encryptText } from '../utils/encryption';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

const LaboratorySearch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // State for search filters
  const [filters, setFilters] = useState({
    laboratoryName: '',
    country: '101', // Default to India
    state: '',
    city: '',
    labId: '',
    userName: '',
    status: '',
    standardId: '',
    certificateNumber: '',
    dealingOfficer: '',
    labType: '',
    applicationStage: '',
    fromDate: null,
    toDate: null,
    submitDate: null,
    amendmentDate: null,
    disciplineId: '',
    groupId: '',
    pincode: '',
    facilityType: ''
  });

  // State for dropdown options
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    states: [],
    cities: [],
    labTypes: [],
    statuses: [],
    disciplines: [],
    dealingOfficers: [],
    applicationStages: [],
    facilityTypes: []
  });

  // State for search results
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
    limit: 25
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table');
  
  const searchResultsRef = useRef(null);

  // Popup state for view details
  const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);

  // Load filter options on component mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Load states when country changes (default India)
  useEffect(() => {
    if (filters.country && filters.country !== '') {
      loadStatesByCountry(filters.country);
    }
  }, [filters.country]);

  // Auto-switch view mode based on screen size
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  const loadFilterOptions = async () => {
    try {
      setFilterOptionsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/laboratory/search/filter-options`);
      
      if (response.data.success) {
        setFilterOptions(response.data.data);
        // After loading filter options, load states for India (default country)
        if (response.data.data.countries.length > 0) {
          loadStatesByCountry('101'); // Load states for India
        }
        console.log('Filter options loaded successfully. India set as default country.');
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
      setError('Failed to load filter options');
    } finally {
      setFilterOptionsLoading(false);
    }
  };

  // Load states by country ID
  const loadStatesByCountry = async (countryId) => {
    if (!countryId || countryId === '') return;
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/laboratory/states/${countryId}`);
      
      if (response.data.success) {
        setFilterOptions(prev => ({
          ...prev,
          states: response.data.data,
          cities: [] // Clear cities when country changes
        }));
        // Clear state and city when country changes
        if (filters.country !== countryId) {
          setFilters(prev => ({ ...prev, state: '', city: '' }));
        }
      }
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  // Load cities by state ID
  const loadCitiesByState = async (stateId) => {
    if (!stateId || stateId === '') return;
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/laboratory/cities/${stateId}`);
      
      if (response.data.success) {
        setFilterOptions(prev => ({
          ...prev,
          cities: response.data.data
        }));
      }
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Handle cascading dropdowns
    if (field === 'country' && value !== '') {
      loadStatesByCountry(value);
    } else if (field === 'state' && value !== '') {
      loadCitiesByState(value);
      // Clear city when state changes
      setFilters(prev => ({ ...prev, city: '' }));
    }
  };

  const handleSearch = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Prepare query parameters
      const queryParams = {
        ...filters,
        page,
        limit: pagination.limit,
        fromDate: filters.fromDate ? filters.fromDate.toISOString().split('T')[0] : '',
        toDate: filters.toDate ? filters.toDate.toISOString().split('T')[0] : '',
        submitDate: filters.submitDate ? filters.submitDate.toISOString().split('T')[0] : '',
        amendmentDate: filters.amendmentDate ? filters.amendmentDate.toISOString().split('T')[0] : ''
      };

      // Remove empty values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === '' || queryParams[key] === null) {
          delete queryParams[key];
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/laboratory/search/advanced`, {
        params: queryParams
      });

      if (response.data.success) {
        setSearchResults(response.data.data);
        setPagination(response.data.pagination);
        setTimeout(() => searchResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      } else {
        setError(response.data.message || 'Search failed');
      }
    } catch (error) {
      console.error('Error performing search:', error);
      setError('An error occurred while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      laboratoryName: '',
      country: '101', // Keep India as default
      state: '',
      city: '',
      labId: '',
      userName: '',
      status: '',
      standardId: '',
      certificateNumber: '',
      dealingOfficer: '',
      labType: '',
      applicationStage: '',
      fromDate: null,
      toDate: null,
      submitDate: null,
      amendmentDate: null,
      disciplineId: '',
      groupId: '',
      pincode:'',
      facilityType: ''
    });
    setSearchResults([]);
    setPagination({
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      limit: 25
    });
    // Reload states for India when clearing filters
    loadStatesByCountry('101');
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    handleSearch(1);
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);

      // Prepare query parameters (same as search but without pagination)
      const queryParams = {
        ...filters,
        fromDate: filters.fromDate ? filters.fromDate.toISOString().split('T')[0] : '',
        toDate: filters.toDate ? filters.toDate.toISOString().split('T')[0] : '',
        submitDate: filters.submitDate ? filters.submitDate.toISOString().split('T')[0] : '',
        amendmentDate: filters.amendmentDate ? filters.amendmentDate.toISOString().split('T')[0] : ''
      };

      // Remove empty values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === '' || queryParams[key] === null) {
          delete queryParams[key];
        }
      });

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/laboratory/search/export-excel`, {
        params: queryParams,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'LaboratorySearchResults.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export data to Excel');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handlers for details popup
  const handleViewDetails = (lab) => {
    setSelectedLab(lab);
    setDetailsPopupOpen(true);
  };

  const handleCloseDetailsPopup = () => {
    setDetailsPopupOpen(false);
    setSelectedLab(null);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const HandleViewCertificate = (CertificateURL) => {
    window.open(CertificateURL, '_blank');
  };

  const handleViewHistory = (lab) => {
    const encryptedAppId = encryptAppId(lab.AppId);
    const encryptedOrgId = encryptText(lab.OrgId);
    navigate(`/main/do/applications/history?appId=${encryptedAppId}&orgId=${encryptedOrgId}`);
  };

  if (filterOptionsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Laboratory Search
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search Filters */}
        <Card sx={{ mb: 3 }}>
          <Accordion expanded={filtersExpanded} onChange={(e, expanded) => setFiltersExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon />
                <Typography variant="h6">Search Filters</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3} onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(1); }}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Laboratory Name"
                    value={filters.laboratoryName}
                    onChange={(e) => handleFilterChange('laboratoryName', e.target.value)}
                    placeholder="Enter laboratory name"
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="CAB ID"
                    value={filters.labId}
                    onChange={(e) => handleFilterChange('labId', e.target.value)}
                    placeholder="Enter CAB ID"
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={filters.userName}
                    onChange={(e) => handleFilterChange('userName', e.target.value)}
                    placeholder="Enter username"
                  />
                </Grid>

                {/* Location Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Location Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={filterOptions.countries}
                    getOptionLabel={(option) => option.name || ''}
                    value={filterOptions.countries.find(country => country.id === filters.country) || null}
                    onChange={(event, newValue) => {
                      handleFilterChange('country', newValue ? newValue.id : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        placeholder="Search and select country"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ id: '', name: 'All States' }, ...filterOptions.states]}
                    getOptionLabel={(option) => option.name || option.State || ''}
                    value={filterOptions.states.find(state => 
                      (state.id || state.StateName) === filters.state) || 
                      (filters.state === '' ? { id: '', name: 'All States' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('state', newValue ? (newValue.id || newValue.StateName) : '');
                    }}
                    isOptionEqualToValue={(option, value) => 
                      (option.id || option.StateName) === (value.id || value.StateName)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        placeholder="Search and select state"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ id: '', name: 'All Cities' }, ...filterOptions.cities]}
                    getOptionLabel={(option) => option.name || ''}
                    value={filterOptions.cities.find(city => 
                      (city.id || city.name) === filters.city) || 
                      (filters.city === '' ? { id: '', name: 'All Cities' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('city', newValue ? (newValue.id || newValue.name) : '');
                    }}
                    isOptionEqualToValue={(option, value) => 
                      (option.id || option.name) === (value.id || value.name)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        placeholder="Search and select city"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                {/* Status and Classification */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Status and Classification
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ Status: '', display: 'All Status' }, ...filterOptions.statuses]}
                    getOptionLabel={(option) => option.display || option.Status || ''}
                    value={filterOptions.statuses.find(status => status.Status === filters.status) || 
                      (filters.status === '' ? { Status: '', display: 'All Status' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('status', newValue ? newValue.Status : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.Status === value.Status}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        placeholder="Search and select status"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ Id: '', LabType: 'All Lab Types' }, ...filterOptions.labTypes]}
                    getOptionLabel={(option) => option.LabType || ''}
                    value={filterOptions.labTypes.find(type => type.Id === filters.labType) || 
                      (filters.labType === '' ? { Id: '', LabType: 'All Lab Types' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('labType', newValue ? newValue.Id : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.Id === value.Id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lab Type"
                        placeholder="Search and select lab type"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ Description: '', display: 'All Stages' }, ...filterOptions.applicationStages]}
                    getOptionLabel={(option) => option.display || option.Description || ''}
                    value={filterOptions.applicationStages.find(stage => stage.Description === filters.applicationStage) || 
                      (filters.applicationStage === '' ? { Description: '', display: 'All Stages' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('applicationStage', newValue ? newValue.Description : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.Description === value.Description}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Application Stage"
                        placeholder="Search and select application stage"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                {/* Advanced Filters */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Advanced Filters
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Standard"
                    value={filters.standardId}
                    onChange={(e) => handleFilterChange('standardId', e.target.value)}
                    placeholder="Enter standard like '2022 or 2017'"
                  />
                </Grid>

                  <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={filters.pincode}
                    onChange={(e) => handleFilterChange('pincode', e.target.value)}
                    placeholder="Enter Pincode "
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <TextField
                    fullWidth
                    label="Certificate Number"
                    value={filters.certificateNumber}
                    onChange={(e) => handleFilterChange('certificateNumber', e.target.value)}
                    placeholder="Enter certificate number"
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ Name: '', OrgId: '', display: 'All Officers' }, ...filterOptions.dealingOfficers]}
                    getOptionLabel={(option) => option.display || option.Name || ''}
                    value={filterOptions.dealingOfficers.find(officer => officer.Name === filters.dealingOfficer) || 
                      (filters.dealingOfficer === '' ? { Name: '', OrgId: '', display: 'All Officers' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('dealingOfficer', newValue ? newValue.Name : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.Name === value.Name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dealing Officer"
                        placeholder="Search and select dealing officer"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={[{ id: '', DisciplineName: 'All Disciplines' }, ...filterOptions.disciplines]}
                    getOptionLabel={(option) => option.DisciplineName || ''}
                    value={filterOptions.disciplines.find(discipline => discipline.id === filters.disciplineId) || 
                      (filters.disciplineId === '' ? { id: '', DisciplineName: 'All Disciplines' } : null)}
                    onChange={(event, newValue) => {
                      handleFilterChange('disciplineId', newValue ? newValue.id : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Discipline"
                        placeholder="Search and select discipline"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                  <Autocomplete
                    options={filterOptions.facilityTypes}
                    getOptionLabel={(option) => option.label || ''}
                    value={filterOptions.facilityTypes.find(facility => facility.value === filters.facilityType) || null}
                    onChange={(event, newValue) => {
                      handleFilterChange('facilityType', newValue ? newValue.value : '');
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Facility Type"
                        placeholder="Search and select facility type"
                        fullWidth
                      />
                    )}
                    clearOnEscape
                    selectOnFocus
                  />
                </Grid>

                {/* Date Range */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Date Range
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Issue From Date"
                    value={filters.fromDate}
                    onChange={(date) => handleFilterChange('fromDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Issue To Date"
                    value={filters.toDate}
                    onChange={(date) => handleFilterChange('toDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Amendment Date"
                    value={filters.amendmentDate}
                    onChange={(date) => handleFilterChange('amendmentDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Submit Date"
                    value={filters.submitDate}
                    onChange={(date) => handleFilterChange('submitDate', date)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                      onClick={() => handleSearch(1)}
                      disabled={loading}
                    >
                      Search Laboratories
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card ref={searchResultsRef}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">
                  Search Results ({pagination.totalRecords} laboratories found)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* View Toggle Buttons */}
                  <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                    <Button
                      variant={viewMode === 'table' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setViewMode('table')}
                      sx={{ borderRadius: 0, px: 2 }}
                    >
                      Table
                    </Button>
                    <Button
                      variant={viewMode === 'card' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setViewMode('card')}
                      sx={{ borderRadius: 0, px: 2 }}
                    >
                      Cards
                    </Button>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportToExcel}
                    disabled={loading || searchResults.length === 0}
                  >
                    {loading ? 'Exporting...' : 'Export to Excel'}
                  </Button>
                </Box>
              </Box>

              {/* Table View */}
              {viewMode === 'table' ? (
                <TableContainer component={Paper}>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 120 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Lab Details
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Laboratory Info
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Location
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Status
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Certificate
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 80 }} align="center">
                          <Typography variant="subtitle2" fontWeight="bold">
                            Actions
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((lab) => (
                        <TableRow key={`${lab.AppId}-${lab.OrgId}`} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                                LAB ID: {lab.LabId || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                Type: {lab.Labtype || 'N/A'}
                              </Typography>
                              {lab.UserName && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                  User: {lab.UserName}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 'medium', lineHeight: 1.2 }}>
                                {lab.LaboratoryName || 'N/A'}
                              </Typography>
                              {lab.DealingOfficer && (
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                  DO: {lab.DealingOfficer}
                                </Typography>
                              )}
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                Applied Date: {formatDate(lab.submitdate1) || formatDate(lab.Applicationdate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.3 }}>
                              {[lab.City, lab.state].filter(Boolean).join(', ') || 'N/A'}
                            </Typography>
                            {lab.District && (
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                {lab.District}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={lab.Description || 'Unknown'}
                              color={getStatusColor(lab.Description)}
                              size="small"
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24,
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {lab.Cert_No ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'medium' }}>
                                  {lab.Cert_No}
                                </Typography>
                                {lab.ToDate && (
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                    Valid: {lab.ToDate}
                                  </Typography>
                                )}
                                {lab.Issue_Date && (
                                  <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                                    Issued: {lab.Issue_Date}
                                  </Typography>
                                )}
                                {lab.CertificateURL && (
                                  <Button variant='contained' size='small' onClick={()=>HandleViewCertificate(lab.CertificateURL)}>
                                    <VisibilityIcon/>
                                    </Button>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No Certificate
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(lab)}
                                sx={{ padding: '4px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View History">
                              <IconButton
                                size="small"
                                onClick={() => handleViewHistory(lab)}
                                sx={{ padding: '4px' }}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                /* Card View */
                <Grid container spacing={2}>
                  {searchResults.map((lab) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`${lab.AppId}-${lab.OrgId}`}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', '&:hover': { boxShadow: 4 } }}>
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          {/* Header with Lab Name and Status */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.2, flex: 1, mr: 1 }}>
                              {lab.LaboratoryName || 'N/A'}
                            </Typography>
                            <Chip
                              label={lab.Status || 'Unknown'}
                              color={getStatusColor(lab.Status)}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                          
                          {/* Lab ID and Type */}
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                              ID: {lab.LabId || lab.ApplicationNo || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                              • {lab.Labtype || 'N/A'}
                            </Typography>
                          </Box>
                          
                          {/* Location */}
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 1, color: 'text.secondary' }}>
                            📍 {[lab.City, lab.State].filter(Boolean).join(', ') || 'Location N/A'}
                          </Typography>
                          
                          {/* Certificate Info */}
                          {lab.Cert_No ? (
                            <Box sx={{ mb: 1, p: 1, bgcolor: 'success.light', borderRadius: 1, opacity: 0.7 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'block' }}>
                                Certificate: {lab.Cert_No}
                              </Typography>
                              {lab.Issue_Date && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  Issue Date: {lab.Issue_Date}
                                </Typography>
                              )}
                              {lab.ToDate && (
                                <Typography variant="caption" color="textSecondary">
                                  Valid till: {lab.ToDate}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" color="textSecondary">
                                No Certificate Available
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Additional Info */}
                          <Box sx={{ fontSize: '0.75rem' }}>
                            {lab.DealingOfficer && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                DO: {lab.DealingOfficer}
                              </Typography>
                            )}
                            {lab.UserName && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                User: {lab.UserName}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary" display="block">
                              Applied: {formatDate(lab.submitdate1) || formatDate(lab.Applicationdate) || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                        
                        {/* Action Buttons */}
                        <Box sx={{ p: 1, pt: 0, display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDetails(lab)}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            View Details
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={() => handleViewHistory(lab)}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            History
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              <TablePagination
                component="div"
                count={pagination.totalRecords}
                page={pagination.currentPage - 1}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        )}

        {/* No Results Message */}
        {!loading && searchResults.length === 0 && pagination.totalRecords === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  No laboratories found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search criteria to find more results.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Laboratory Details Popup */}
      <Dialog 
        open={detailsPopupOpen} 
        onClose={handleCloseDetailsPopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1 
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Laboratory Details
          </Typography>
          <IconButton onClick={handleCloseDetailsPopup} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {selectedLab && (
            <Box sx={{ p: 3 }}>
              {/* Basic Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Application ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.appid || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Lab ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.LabId || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Laboratory Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.LaboratoryName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Lab Type</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Labtype || selectedLab.TypeOfLaboratory || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Username</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.UserName || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Assessment Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Assessment Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Standard</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Standard || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Assessment Type</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.AssessmentTypes || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Assessment Type ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.AssessmentTypes || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Stage</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Stage || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Description</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Description || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Dealing Officer</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.DoAlloted || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Discipline</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.DisciplineName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Group</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.GroupName || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Contact Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Contact Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Contact Person</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.ContactPerson || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Contact Email</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.ContactEmail || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Contact Mobile</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.ContactMobile || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Landline</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.LandLine || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Address Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Address Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Primary Address</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.PrimeAddress || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">City</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.City || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">State</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.State || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="textSecondary">Pincode</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Pincode || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Certificate Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Certificate Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Certificate Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Cert_No || 'Not Available'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Issue Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.Issue_Date || 'Not Available'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Valid Till</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.ToDate || 'Not Available'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Amendment Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.AmendmentDate==="01-01-1900" ? 'Not Available' : selectedLab.AmendmentDate || 'Not Available'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Date Information Section */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                Important Dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Submit Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {selectedLab.submitdate1 ? 
                      formatDate(selectedLab.submitdate1) : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selectedLab && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => { handleCloseDetailsPopup(); handleViewHistory(selectedLab); }}
            >
              View History
            </Button>
          )}
          <Button onClick={handleCloseDetailsPopup} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default LaboratorySearch;